import { formatCopy } from '../../copy/format.js';
import copy from '../../copy/en/auth.json' with { type: 'json' };
import { useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useSession } from "./SessionProvider.js";
import { isJoinConfirmationFailure, joinConfirmationNotice } from "./joinConfirmation.js";
import { Button } from "../components/ui/Button.js";
import { Callout } from "../components/ui/Callout.js";
import { Field, Input } from "../components/ui/Form.js";
import { Brand } from "../shell/Brand.js";
import { ModeToggle } from "../shell/TopBar.js";
import { useTheme } from "../theme/index.js";

export function LoginPage() {
  const { authMode, status, joinConfirmation, notice, clearNotice, signOut, signInWithPassword, signInWithMockToken, config } = useSession();
  const { theme } = useTheme();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<"email" | "password" | "token" | null>(null);
  const emailInput = useRef<HTMLInputElement>(null), passwordInput = useRef<HTMLInputElement>(null), tokenInput = useRef<HTMLInputElement>(null);
  const submitting = useRef(false);

  const configured = Boolean(config.supabaseUrl && config.supabasePublishableKey);
  const confirmationNotice = joinConfirmationNotice(joinConfirmation);
  const mustSignOut = status === "signed-in" && isJoinConfirmationFailure(joinConfirmation);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (submitting.current) return;
    setError(null);
    setFieldError(null);
    clearNotice();
    if (authMode === "mock") {
      if (!token.trim()) {
        setError(copy.LoginPage.enterTheLocalAccessToken);
        setFieldError("token"); tokenInput.current?.focus();
        return;
      }
      signInWithMockToken(token);
      return;
    }
    if (!email.trim() || !emailInput.current?.validity.valid) {
      setError(copy.LoginPage.enterAValidEmailAddress);
      setFieldError("email"); emailInput.current?.focus();
      return;
    }
    if (!password) {
      setError(copy.LoginPage.enterYourPassword);
      setFieldError("password"); passwordInput.current?.focus();
      return;
    }
    submitting.current = true;
    setBusy(true);
    try {
      const result = await signInWithPassword(email.trim(), password);
      if (result.error) setError(result.error);
    } catch {
      setError(copy.LoginPage.signInCouldNotBeCompletedCheck);
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="fullpage">
      <div className="fullpage__mode">
        <ModeToggle />
      </div>
      <div className="fullpage__card">
        <div className="fullpage__hero">
          <Brand size="lg" to={null} />
          <h1 className="fullpage__title">{copy.LoginPage.staffSignIn}</h1>
          <p className="fullpage__sub">{formatCopy(copy.LoginPage.accountHint, { productName: theme.productName })}</p>
        </div>
        {confirmationNotice || notice ? <Callout tone="info">{confirmationNotice ?? notice}</Callout> : null}
        {mustSignOut ? <Button variant="primary" onClick={() => void signOut(copy.LoginPage.youHaveBeenSignedOutReopenYour)}>{copy.LoginPage.signOut}</Button> : <form className="login-form" onSubmit={(e) => void submit(e)} noValidate style={{ marginTop: confirmationNotice || notice ? "var(--space-4)" : 0 }}>
          {authMode === "mock" ? (
            <Field label={copy.LoginPage.accessToken} error={fieldError === "token" ? error : null} help={copy.LoginPage.mockAuthIsEnabledOnThisServer}>
              {(control) => (
                <Input
                  {...control}
                  ref={tokenInput}
                  type="password"
                  autoComplete="off"
                  placeholder={copy.LoginPage.pastePortalMockToken}
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                />
              )}
            </Field>
          ) : (
            <>
              {!configured ? (
                <Callout tone="warning" title={copy.LoginPage.signInIsNotConfigured}>{copy.LoginPage.signInIsTemporarilyUnavailableContactAn}</Callout>
              ) : null}
              <Field label={copy.LoginPage.email} error={fieldError === "email" ? error : null}>
                {(control) => (
                  <Input
                    {...control}
                    ref={emailInput}
                    type="email"
                    autoComplete="username"
                    spellCheck={false}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={busy}
                  />
                )}
              </Field>
              <Field label={copy.LoginPage.password} error={fieldError === "password" ? error : null}>
                {(control) => (
                  <Input
                    {...control}
                    ref={passwordInput}
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={busy}
                  />
                )}
              </Field>
            </>
          )}
          {error && !fieldError ? <Callout tone="danger">{error}</Callout> : null}
          <Button type="submit" variant="primary" loading={busy} disabled={authMode === "jwt" && !configured}>{copy.LoginPage.signIn}</Button>
          {authMode === "jwt" && configured ? <Link to="/forgot-password">{copy.LoginPage.forgotPassword}</Link> : null}
        </form>}
      </div>
    </div>
  );
}
